import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CsrAPI } from "../apis/CsrAPI";
import {
  missingRequiredFields,
  requiredFieldMessage,
} from "../utils/patientNarrativeRequiredFields";
import {
  toSectionPayload,
  withDefaultAnswers,
} from "../utils/patientNarrativeWizardPayload";

/**
 * Owns the full GET -> edit -> PATCH -> canonical-replace lifecycle for one
 * Patient Narrative wizard section. This is deliberately a hook, not a
 * context/provider - each page owns exactly one section, and nothing here
 * needs to be shared across pages or survive navigation (mirrors CSR's
 * "one page owns one section" wiring, without CSR's react-reactive-form
 * `wizardForm` machinery, which Patient Narrative does not use or share).
 *
 * The backend stores exactly what it is sent and reports nothing about
 * completeness (that is only checked when generation starts), so:
 *
 * - answers start from the saved `sectionData` with the section's defaults
 *   filled in for anything never saved (see patientNarrativeWizardPayload);
 * - `save()` always PATCHes the whole page state - PATCH is a full
 *   replacement - and the response (which may differ, e.g. de-duplicated
 *   `other_events`) replaces the local copy;
 * - required-field errors are worked out locally from the same rules the
 *   backend applies before generation.
 *
 * RBAC lives here rather than in each page so one section is gated in one
 * place: without `canRead` (pn:wizards:read) the GET is never sent and the
 * page starts from the defaults alone, and without `canSave`
 * (pn:wizards:update on top of the read) `save()` resolves without PATCHing.
 *
 * Errors are only *displayed* once a field has been touched or
 * `revealErrors()` has been called (Save/Next), so a new section doesn't open
 * as a page full of failures; `missingRequiredFields` stays live throughout
 * so callers can gate Next.
 */
export default function usePatientNarrativeWizardSection(
  patientNarrativeId,
  section,
  { canRead = true, canSave = true } = {}
) {
  const [answers, setAnswers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Which fields the user has actually changed, and whether a save/next
  // attempt has asked for everything outstanding to be shown.
  const [touchedFields, setTouchedFields] = useState(() => new Set());
  const [errorsRevealed, setErrorsRevealed] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!patientNarrativeId || !section) return undefined;
    // Without pn:wizards:read the section is never fetched; the defaults
    // stand in for the response so the page renders instead of waiting on a
    // GET that will never happen.
    if (!canRead) {
      setAnswers(withDefaultAnswers(section, {}));
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    CsrAPI.getPatientNarrativeWizardSection({ patientNarrativeId, section })
      .then(sectionData => {
        if (!cancelled) setAnswers(withDefaultAnswers(section, sectionData));
      })
      .catch(err => {
        // `answers` stays null: PATCH replaces the whole section, so a form
        // built without the saved answers must never be offered for saving.
        // Pages show the load error in place of the form.
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [patientNarrativeId, section, canRead]);

  // Updates one or more fields in the local (not-yet-saved) copy. Pass
  // several keys at once (e.g. a value and its confirmation) so they change
  // together.
  const updateFields = useCallback(patch => {
    setAnswers(prev => ({ ...prev, ...patch }));
    setTouchedFields(prev => {
      const next = new Set(prev);
      Object.keys(patch).forEach(key => next.add(key));
      return next;
    });
  }, []);

  // Called when the user tries to move on (Save, or Next - which saves
  // first): from then on every outstanding error is shown, not just the ones
  // for fields they happened to touch.
  const revealErrors = useCallback(() => setErrorsRevealed(true), []);

  // Live against the local answers, so Next re-enables as soon as the last
  // mandatory question is answered.
  const missingRequired = useMemo(
    () => missingRequiredFields(section, answers),
    [section, answers]
  );

  /** The error to show under `fieldName`, or null. */
  const errorForField = useCallback(
    fieldName => {
      if (!errorsRevealed && !touchedFields.has(fieldName)) return null;
      if (!missingRequired.includes(fieldName)) return null;
      return {
        field: fieldName,
        code: "required",
        message: requiredFieldMessage(fieldName),
      };
    },
    [errorsRevealed, missingRequired, touchedFields]
  );

  const save = useCallback(async () => {
    // Read-only users: Save is disabled and Next navigates without saving,
    // so reaching here at all means the edits are simply dropped. Without
    // loaded answers there is nothing safe to send (see the GET above).
    if (!canSave || !answers) return answers;

    setSaving(true);
    setError(null);
    try {
      const sectionData = await CsrAPI.savePatientNarrativeWizardSection({
        patientNarrativeId,
        section,
        sectionData: toSectionPayload(section, answers),
      });
      if (mountedRef.current) setAnswers(sectionData);
      return sectionData;
    } catch (err) {
      if (mountedRef.current) setError(err);
      throw err;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [patientNarrativeId, section, answers, canSave]);

  return {
    answers,
    loading,
    saving,
    error,
    updateFields,
    save,
    errorForField,
    revealErrors,
    missingRequiredFields: missingRequired,
    hasMissingRequiredFields: missingRequired.length > 0,
  };
}
